import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Fade,
  Radio,
  RadioGroup,
  Stack,
  Modal,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./InvoiceProcess.module.css";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_ENDPOINT } from "../../../services/api/resources";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_FORMAT_LABEL,
  isDocumentUploadFile,
} from "../../../utils/documentUploadTypes";

const VERIFICATION_CHECKS = [
  { key: "po_number_match", label: "PO number" },
  { key: "item_count_match", label: "Item count" },
  { key: "quantity_match", label: "Quantity" },
  { key: "item_name_match", label: "Item name" },
  { key: "item_amount_match", label: "Item amount" },
  { key: "description_match", label: "Description" },
  { key: "total_amount_match", label: "Total amount" },
];

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatReadableLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\bpo\b/gi, "PO")
    .replace(/\bpi\b/gi, "PI")
    .replace(/\bgstin\b/gi, "GSTIN");

const getDocumentValidationError = (file, label) => {
  if (!file) {
    return `Please upload the ${label} document.`;
  }

  if (!isDocumentUploadFile(file)) {
    return `${label} must be one of: ${DOCUMENT_UPLOAD_FORMAT_LABEL}.`;
  }

  if (!(file.size > 0)) {
    return `${label} file looks empty. Please choose the document again.`;
  }

  return "";
};

const appendPdfFile = (formData, fieldName, file) => {
  formData.append(fieldName, file, file?.name || `${fieldName}.pdf`);
};

const formatApiMessage = (message) => {
  if (!message) return "Something went wrong.";

  if (Array.isArray(message)) {
    return message.map(formatApiMessage).join("<br/>");
  }

  if (typeof message === "object") {
    return Object.entries(message)
      .map(
        ([key, value]) =>
          `<strong>${escapeHtml(formatReadableLabel(key))}:</strong> ${formatApiMessage(
            value
          )}`
      )
      .join("<br/>");
  }

  return escapeHtml(message);
};

const displayOrDash = (value) => {
  if (value === 0) return "0";
  if (value === null || value === undefined) return "-";
  const text = String(value);
  return text.trim() ? text : "-";
};

const parseMoneyNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const normalized = String(value)
    .replace(/[, ]/g, "")
    .replace(/[^\d.-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatAmount = (amount, currency = "INR") => {
  const numericAmount = parseMoneyNumber(amount);
  const safeCurrency = currency || "INR";
  const currencyLabel =
    String(safeCurrency).toUpperCase() === "INR" ? "₹" : safeCurrency;

  if (!Number.isFinite(numericAmount)) {
    const displayed = displayOrDash(amount);
    if (displayed === "-") return "-";
    return `${escapeHtml(currencyLabel)}\u00A0${escapeHtml(displayed)}`;
  }

  const formatted = numericAmount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${escapeHtml(currencyLabel)}\u00A0${formatted}`;
};

const isMatchedStatus = (status) =>
  String(status || "").toLowerCase() === "matched";

const getItemReference = (item) =>
  item?.item_code ||
  item?.invoice_part_number ||
  item?.po_part_number ||
  item?.part_number ||
  item?.item_name ||
  item?.description ||
  "Item";

const buildMismatchDetails = (key, check, apiData) => {
  if (key === "item_count_match") {
    return `Invoice items: ${escapeHtml(
      check.invoice_item_count ?? "-"
    )}, PO items: ${escapeHtml(check.po_item_count ?? "-")}`;
  }

  if (key === "total_amount_match") {
    return `Invoice total: ${formatAmount(
      check.invoice_total_amount ?? apiData?.grand_total,
      apiData?.currency
    )}, PO total: ${formatAmount(
      check.po_total_amount,
      apiData?.currency
    )}, Difference: ${formatAmount(check.difference, apiData?.currency)}`;
  }

  if (Array.isArray(check?.mismatched_items) && check.mismatched_items.length) {
    return check.mismatched_items
      .slice(0, 3)
      .map((item) => {
        const itemReference = escapeHtml(getItemReference(item));

        if (key === "quantity_match") {
          return `${itemReference}: Invoice qty ${escapeHtml(
            item.invoice_quantity ?? "-"
          )}, PO qty ${escapeHtml(item.po_quantity ?? "-")}`;
        }

        if (key === "item_name_match") {
          return `${itemReference}: Invoice "${escapeHtml(
            item.invoice_item_name ?? item.item_name ?? "-"
          )}", PO "${escapeHtml(item.po_item_name ?? "-")}"`;
        }

        if (key === "item_amount_match") {
          return `${itemReference}: Invoice amount ${formatAmount(
            item.invoice_total_amount,
            apiData?.currency
          )}, PO amount ${formatAmount(
            item.po_total_amount,
            apiData?.currency
          )}`;
        }

        if (key === "description_match") {
          return `${itemReference}: Description match score ${escapeHtml(
            item.description_match_score ?? "-"
          )}`;
        }

        return itemReference;
      })
      .join("<br/>");
  }

  return `Status: ${escapeHtml(check?.status || "Not matched")}`;
};

const getVerificationIssues = (apiData) => {
  const summary = apiData?.verification_summary;
  if (!summary) return [];

  return VERIFICATION_CHECKS.reduce((issues, { key, label }) => {
    const check = summary[key];

    if (!check || isMatchedStatus(check.status)) {
      return issues;
    }

    issues.push(
      `<strong>${label} mismatch:</strong> ${buildMismatchDetails(
        key,
        check,
        apiData
      )}`
    );

    return issues;
  }, []);
};

const getUploadErrorMessage = (apiData) =>
  apiData?.error || apiData?.message || apiData?.response || apiData?.detail;

const isUploadSuccess = (apiData) =>
  !apiData?.status || String(apiData.status).toLowerCase() === "success";

const hasVerificationSummary = (apiData) =>
  Boolean(
    apiData?.verification_summary &&
      Object.keys(apiData.verification_summary).length
  );

const buildVerificationDescription = (apiData) => {
  const summary = apiData?.verification_summary || {};
  const matchedChecks = VERIFICATION_CHECKS.filter(({ key }) =>
    isMatchedStatus(summary[key]?.status)
  ).map(({ label }) => label);
  const invoiceNumber = apiData?.invoice_number || "-";
  const poNumber = apiData?.pi_po_number || summary.po_number_match?.po_number || "-";
  const itemCount =
    apiData?.items?.length ?? summary.item_count_match?.invoice_item_count ?? "-";

  if (!hasVerificationSummary(apiData)) {
    return [
      `Invoice <strong>${escapeHtml(invoiceNumber)}</strong> was extracted successfully.`,
      `PO number: <strong>${escapeHtml(poNumber)}</strong>.`,
      `Items: ${escapeHtml(itemCount)} | Total: ${formatAmount(
        apiData?.grand_total,
        apiData?.currency
      )}`,
    ].join("<br/>");
  }

  return [
    `Invoice <strong>${escapeHtml(invoiceNumber)}</strong> matches PO <strong>${escapeHtml(
      poNumber
    )}</strong>.`,
    `Matched checks: ${escapeHtml(
      matchedChecks.length ? matchedChecks.join(", ") : "Invoice verification"
    )}.`,
    `Items: ${escapeHtml(itemCount)} | Total: ${formatAmount(
      apiData?.grand_total,
      apiData?.currency
    )}`,
  ].join("<br/>");
};

const buildVerificationFailureDescription = (apiData, issues) => {
  const displayedIssues = issues.slice(0, 4);
  const remainingCount = issues.length - displayedIssues.length;
  const remainingMessage =
    remainingCount > 0
      ? `<br/>${remainingCount} more mismatch(es) found. Please review invoice items.`
      : "";

  return [
    "Please review the invoice before saving.",
    ...displayedIssues,
  ].join("<br/>") + remainingMessage;
};

const buildUploadFailureDescription = (apiData, uploadError) => {
  if (uploadError) {
    return formatApiMessage(uploadError);
  }

  const verificationIssues = getVerificationIssues(apiData);

  if (verificationIssues.length) {
    return buildVerificationFailureDescription(apiData, verificationIssues);
  }

  return "Invoice upload failed.";
};

const InvoiceStatusCard = ({ variant, title, description, onClose }) => {
  const isSuccess = variant === "success";
  const StatusIcon = isSuccess
    ? CheckCircleOutlineRoundedIcon
    : ErrorOutlineRoundedIcon;

  return (
    <Box
      className={`${styles.statusModalCard} ${
        isSuccess ? styles.statusModalCardSuccess : styles.statusModalCardFailed
      }`}
    >
      <Box className={styles.statusAccent} />
      <Box className={styles.statusIconWrap}>
        <StatusIcon className={styles.statusIcon} />
      </Box>
      <Box
        component="span"
        className={`${styles.statusPill} ${
          isSuccess ? styles.statusPillSuccess : styles.statusPillFailed
        }`}
      >
        {isSuccess ? "Verified" : "Failed"}
      </Box>
      <h2 className={styles.statusTitle}>{title}</h2>
      {description && (
        <Box
          className={styles.statusDescription}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
      <Button
        variant="contained"
        onClick={onClose}
        className={styles.statusAction}
      >
        Okay
      </Button>
    </Box>
  );
};

const AddInvoice = ({ onSave, onCancel, VendorToken }) => {
  const loginData = useSelector((state) => state.loginData.value);
  const authToken =
    loginData?.auth_token || loginData?.access_token || loginData?.token || "";
  const orgToken =
    loginData?.organization_token ||
    loginData?.org_token ||
    loginData?.user?.organization_token ||
    loginData?.user?.token ||
    VendorToken ||
    "";
  const [documentMode, setDocumentMode] = useState("invoice_only"); // "invoice_only" | "invoice_and_po"
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [poFile, setPoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [openLineItemModal, setOpenLineItemModal] = useState(false);
  const [itemsTab, setItemsTab] = useState("invoice"); // "invoice" | "po"
  const [saving, setSaving] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalContent, setStatusModalContent] = useState({
    title: "",
    description: "",
    variant: "failed",
  });

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    setStatusModalContent({
      title: "",
      description: "",
      variant: "failed",
    });
  };

  const showErrorModal = (message) => {
    setStatusModalContent({
      title: "Error!",
      description: formatApiMessage(message),
      variant: "failed",
    });
    setStatusModalOpen(true);
  };

  const showStatusModal = ({ title, description, variant }) => {
    setStatusModalContent({
      title,
      description,
      variant,
    });
    setStatusModalOpen(true);
  };

  const showInvoiceVerificationModal = (apiData) => {
    const verificationIssues = getVerificationIssues(apiData);

    if (verificationIssues.length) {
      showStatusModal({
        title: "PO and Invoice Mismatch",
        description: buildVerificationFailureDescription(
          apiData,
          verificationIssues
        ),
        variant: "failed",
      });
      return;
    }

    showStatusModal({
      title: hasVerificationSummary(apiData)
        ? "PO and Invoice Matched"
        : "Invoice Extracted Successfully",
      description: buildVerificationDescription(apiData),
      variant: "success",
    });
  };

  const handleDocumentDrop = (event, type) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file || !isDocumentUploadFile(file)) return;

    if (type === "po") {
      setPoFile(file);
    } else {
      setInvoiceFile(file);
    }
    setExtractedData(null);
    setOpenLineItemModal(false);
  };

  const handleDocumentDragOver = (event) => {
    event.preventDefault();
  };

  const mapInvoiceResponse = (apiData) => ({
    po_id: apiData.pi_po_number,
    invoice_no: apiData.invoice_number,
    invoice_date: apiData.invoice_date,
    invoice_amount: apiData.grand_total,
    status: apiData.status,
    invoice_token: apiData.invoice_token,
    grn_token: apiData.grn_token,
    currency: apiData.currency,
    vendor_gstin: apiData.vendor_gstin || apiData.vendor_gstn || apiData.gstin,
    verification_summary: apiData.verification_summary || {},
    purchase_order_no: apiData.purchase_order_no,
    purchase_order_date: apiData.purchase_order_date,
    purchase_order_total: apiData.purchase_order_total,
    purchase_order_currency: apiData.purchase_order_currency,
    po_data: Array.isArray(apiData.po_data) ? apiData.po_data : [],
    po_items:
      apiData.po_items ||
      apiData.po_line_items ||
      apiData.purchase_order_items ||
      apiData?.po_data?.items ||
      apiData?.po_data?.line_items ||
      [],
    items_raw: apiData.items || [],
    line_items: (apiData.items || []).map((item, index) => ({
      slNo: index + 1,
      code: displayOrDash(item.item_code),
      name: displayOrDash(item.item_name),
      description: displayOrDash(item.description),
      hsn: displayOrDash(item.hsn),
      qty: item.quantity ?? "-",
      unitRate: displayOrDash(item.unit_price),
      totalRate: displayOrDash(item.total_amount),
      tax: item.tax_rate ?? "-",
      taxableAmount: displayOrDash(item.total_amount),
    })),
  });

  const getPoLineItems = (data) => {
    if (!data) return [];

    const candidates = [];
    if (Array.isArray(data.po_items) && data.po_items.length) {
      candidates.push(...data.po_items);
    }
    if (Array.isArray(data.po_data) && data.po_data.length) {
      data.po_data.forEach((row) => {
        if (Array.isArray(row?.line_items) && row.line_items.length) {
          candidates.push(...row.line_items);
        }
      });
    }

    return candidates
      .filter(Boolean)
      .map((item, index) => ({
        slNo: index + 1,
        code: displayOrDash(item.item_code),
        name: displayOrDash(item.item_name),
        description: displayOrDash(item.description),
        qty: item.quantity ?? "-",
        unitRate: displayOrDash(item.unit_price),
        total: displayOrDash(item.total_amount),
      }));
  };

  const handleSaveInvoice = async () => {
    if (!extractedData || !invoiceFile || !VendorToken) return;

    setSaving(true);
    try {
      const invoiceValidationError = getDocumentValidationError(
        invoiceFile,
        "Invoice"
      );
      if (invoiceValidationError) {
        showErrorModal(invoiceValidationError);
        return;
      }

      if (documentMode === "invoice_and_po") {
        const poValidationError = getDocumentValidationError(
          poFile,
          "Purchase order"
        );
        if (poValidationError) {
          showErrorModal(poValidationError);
          return;
        }
      }

      const isInvoiceAndPo = documentMode === "invoice_and_po";

      const res = isInvoiceAndPo
        ? await axios.post(
            API_ENDPOINT.save_vendor_po_invoice_document_data,
            {
              vendor_token: VendorToken,
              invoice_number: extractedData.invoice_no || "",
              pi_po_number: extractedData.po_id || "",
              invoice_date: extractedData.invoice_date || "",
              vendor_gstin: extractedData.vendor_gstin || "",
              grand_total: (() => {
                const n = parseMoneyNumber(extractedData.invoice_amount);
                if (Number.isFinite(n)) return n.toFixed(2);
                return extractedData.invoice_amount ?? "";
              })(),
              currency: extractedData.currency || "",
              items: extractedData.items_raw || [],
              po_data: extractedData.po_data || [],
            },
            authToken
              ? {
                  headers: { Authorization: `Bearer ${authToken}` },
                }
              : undefined
          )
        : await axios.post(
            API_ENDPOINT.save_vendor_invoice_document_data,
            (() => {
              const formData = new FormData();
              formData.append("vendor_token", VendorToken);
              appendPdfFile(formData, "invoice_pdf", invoiceFile);
              formData.append("invoice_number", extractedData.invoice_no || "");
              formData.append("pi_po_number", extractedData.po_id || "");
              formData.append("invoice_date", extractedData.invoice_date || "");
              if (extractedData.vendor_gstin) {
                formData.append("vendor_gstin", extractedData.vendor_gstin);
              }
              formData.append("grand_total", extractedData.invoice_amount || "");
              formData.append("currency", extractedData.currency || "");
              formData.append(
                "items",
                JSON.stringify(extractedData.items_raw || [])
              );
              return formData;
            })(),
            authToken
              ? {
                  headers: { Authorization: `Bearer ${authToken}` },
                }
              : undefined
          );

      if (res?.data?.error) {
        showErrorModal(res.data.error);
        return;
      }

      onSave({
        ...extractedData,
        invoice_file: invoiceFile.name,
        upload_mode: documentMode,
        po_file: poFile?.name || null,
      });
    } catch (error) {
      console.error("Save invoice failed", error);
      showErrorModal(
        error?.response?.data?.error ||
          error?.response?.data?.response ||
          error?.message
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUploadAndExtract = async () => {
    if (!invoiceFile || !VendorToken) return;

    const invoiceValidationError = getDocumentValidationError(
      invoiceFile,
      "Invoice"
    );
    if (invoiceValidationError) {
      showErrorModal(invoiceValidationError);
      return;
    }

    if (documentMode === "invoice_and_po") {
      const poValidationError = getDocumentValidationError(
        poFile,
        "Purchase order"
      );
      if (poValidationError) {
        showErrorModal(poValidationError);
        return;
      }
    }

    // Hide stale extracted data while a new extraction is in progress.
    setExtractedData(null);
    setOpenLineItemModal(false);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("vendor_token", VendorToken);
      formData.append("org_token", orgToken);
      appendPdfFile(formData, "invoice_pdf", invoiceFile);

      const endpoint =
        documentMode === "invoice_and_po"
          ? API_ENDPOINT.upload_vendor_po_invoice_documents
          : API_ENDPOINT.upload_vendor_invoice_document;

      if (documentMode === "invoice_and_po") {
        appendPdfFile(formData, "po_pdf", poFile);
      }

      const res = await axios.post(
        endpoint,
        formData,
        authToken
          ? {
              headers: { Authorization: `Bearer ${authToken}` },
            }
          : undefined
      );

      const apiData = res.data;
      const uploadError = getUploadErrorMessage(apiData);

      if (!apiData || uploadError || !isUploadSuccess(apiData)) {
        showStatusModal({
          title: "Invoice Verification Failed",
          description: buildUploadFailureDescription(apiData, uploadError),
          variant: "failed",
        });
        return;
      }

      setExtractedData(mapInvoiceResponse(apiData));
      setItemsTab("invoice");
      showInvoiceVerificationModal(apiData);
    } catch (error) {
      console.error("Invoice upload failed", error);
      showErrorModal(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.response ||
          error?.message ||
          "Invoice upload failed."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className={styles.pageContainer}
      sx={{
        fontFamily: "gilroyMedium",
        "& .MuiTableCell-root": { fontFamily: "gilroyMedium" },
        "& .MuiButton-root": { fontFamily: "gilroySemiBold" },
        "& .MuiDialogTitle-root": { fontFamily: "gilroyBold" },
        "& .MuiTypography-root": { fontFamily: "gilroyMedium" },
      }}
    >
      {/* HEADER */}
      <Box className={styles.pageHeader}>
        <h1 className={styles.pageTitle} style={{ fontFamily: "gilroyBold" }}>
          Upload Documents
        </h1>
        <Button
          variant="contained"
          onClick={onCancel}
          sx={{ fontWeight: 600, fontSize: "13px" }}
        >
          Back
        </Button>
      </Box>

      {/* MODE SELECTOR */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
        }}
      >
        <p className={styles.uploadTitle} style={{ marginBottom: 8 }}>
          Select documents to upload
        </p>
        <FormControl>
          <RadioGroup
            row
            value={documentMode}
            onChange={(e) => {
              const next = e.target.value;
              setDocumentMode(next);
              if (next !== "invoice_and_po") {
                setPoFile(null);
              }
              setExtractedData(null);
              setOpenLineItemModal(false);
              setItemsTab("invoice");
            }}
            sx={{
              "& .MuiFormControlLabel-label": {
                fontFamily: "gilroyMedium",
                fontSize: "16px",
              },
              "& .MuiRadio-root": {
                padding: "6px",
              },
            }}
          >
            <FormControlLabel
              value="invoice_only"
              control={<Radio />}
              label="Invoice only"
            />
            <FormControlLabel
              value="invoice_and_po"
              control={<Radio />}
              label="Invoice + PO"
            />
          </RadioGroup>
        </FormControl>
        {documentMode === "invoice_and_po" && (
          <p className={styles.uploadHint} style={{ marginTop: 6 }}>
            Use this when the PO is not available in the customer PO list. Invoice
            and PO will be extracted and matched together.
          </p>
        )}
      </Box>

      {/* UPLOADERS */}
      <Stack
        spacing={2.5}
        direction={{
          xs: "column",
          md: documentMode === "invoice_and_po" ? "row" : "column",
        }}
        sx={{ mt: 3, alignItems: "stretch" }}
      >
        {documentMode === "invoice_and_po" && (
          <Box
            className={styles.uploadBox}
            sx={{ flex: 1 }}
            onDrop={(event) => handleDocumentDrop(event, "po")}
            onDragOver={handleDocumentDragOver}
          >
            <p className={styles.uploadTitle}>Purchase Order Document</p>
            <p className={styles.uploadHint}>
              Drag & drop purchase order document here
            </p>
            <p className={styles.uploadHint}>
              Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL}
            </p>

            <Button variant="outlined" component="label">
              Browse File
              <input
                type="file"
                hidden
                accept={DOCUMENT_UPLOAD_ACCEPT}
                onClick={(e) => {
                  e.target.value = "";
                }}
                onChange={(e) => {
                  setPoFile(e.target.files[0]);
                  setExtractedData(null);
                  setOpenLineItemModal(false);
                }}
              />
            </Button>

            {poFile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                }}
              >
                <p className={styles.fileName} style={{ margin: 0 }}>
                  {poFile.name}
                </p>
                <IconButton
                  size="small"
                  aria-label="Clear PO file"
                  sx={{ color: "#dc2626" }}
                  onClick={() => {
                    setPoFile(null);
                    setExtractedData(null);
                    setOpenLineItemModal(false);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        )}

        <Box
          className={styles.uploadBox}
          sx={{ flex: 1 }}
          onDrop={(event) => handleDocumentDrop(event, "invoice")}
          onDragOver={handleDocumentDragOver}
        >
          <p className={styles.uploadTitle}>Invoice Document</p>
          <p className={styles.uploadHint}>Drag & drop invoice document here</p>
          <p className={styles.uploadHint}>
            Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL}
          </p>

          <Button variant="outlined" component="label">
            Browse File
            <input
              type="file"
              hidden
              accept={DOCUMENT_UPLOAD_ACCEPT}
              onClick={(e) => {
                e.target.value = "";
              }}
              onChange={(e) => {
                setInvoiceFile(e.target.files[0]);
                setExtractedData(null);
                setOpenLineItemModal(false);
              }}
            />
          </Button>

          {invoiceFile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1,
              }}
            >
              <p className={styles.fileName} style={{ margin: 0 }}>
                {invoiceFile.name}
              </p>
              <IconButton
                size="small"
                aria-label="Clear invoice file"
                sx={{ color: "#dc2626" }}
                onClick={() => {
                  setInvoiceFile(null);
                  setPoFile(null);
                  setExtractedData(null);
                  setOpenLineItemModal(false);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </Stack>

      {/* ACTION BUTTONS */}
      <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          disabled={
            !invoiceFile ||
            loading ||
            (documentMode === "invoice_and_po" && !poFile)
          }
          onClick={handleUploadAndExtract}
          sx={{
            fontWeight: 700,
            padding: "10px 10px",
            textTransform: "none",
            fontSize: "12px",
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Upload & Extract"
          )}
        </Button>

        <Button
          variant="outlined"
          disabled={!invoiceFile || loading}
          onClick={() => {
            setInvoiceFile(null);
            setPoFile(null);
            setExtractedData(null);
            setOpenLineItemModal(false);
          }}
          sx={{
            fontWeight: 700,
            padding: "10px 10px",
            textTransform: "none",
            fontSize: "12px",
          }}
        >
          Clear File
        </Button>
      </Box>

      {/* EXTRACTED DATA TABLE */}
      {extractedData && (
        <Box sx={{ mt: 5 }}>
          <Box className={styles.tableHeader}>
            <h2 className={styles.sectionTitle}>
              {documentMode === "invoice_and_po"
                ? "Extracted Data"
                : "Extracted Invoice Data"}
            </h2>
            <Button
              variant="contained"
              disabled={saving}
              sx={{
                fontWeight: 700,
                fontSize: "13px",
                textTransform: "none",
                minWidth: "90px",
              }}
              onClick={handleSaveInvoice}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    { label: "PO ID" },
                    { label: "Supplier Invoice ID" },
                    { label: "Invoice Date" },
                    { label: "Invoice Amount", align: "right" },
                    ...(documentMode === "invoice_and_po"
                      ? [{ label: "PO Date" }, { label: "PO Total", align: "right" }]
                      : []),
                    { label: "Action" },
                  ].map((h) => (
                    <TableCell
                      key={h.label}
                      align={h.align}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#e0e9ee",
                        fontSize: "15px",
                        whiteSpace: h.align ? "nowrap" : undefined,
                      }}
                    >
                      {h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow hover>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {displayOrDash(extractedData.po_id)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {displayOrDash(extractedData.invoice_no)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>
                    {displayOrDash(extractedData.invoice_date)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#075b97",
                      fontFamily: "gilroyBold",
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatAmount(extractedData.invoice_amount, extractedData.currency)}
                  </TableCell>

                  {documentMode === "invoice_and_po" && (
                    <>
                      <TableCell sx={{ fontSize: "14px" }}>
                        {displayOrDash(extractedData.purchase_order_date)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#075b97",
                          fontFamily: "gilroyBold",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatAmount(
                          extractedData.purchase_order_total,
                          extractedData.purchase_order_currency ||
                            extractedData.currency
                        )}
                      </TableCell>
                    </>
                  )}

                  <TableCell>
                    <Button
                      variant="outlined"
                      size="smalargell"
                      sx={{ fontWeight: 600 }}
                      onClick={() => setOpenLineItemModal(true)}
                    >
                      View Items
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* LINE ITEMS MODAL */}
      <Dialog
        open={openLineItemModal}
        onClose={() => setOpenLineItemModal(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiTableCell-root": { fontFamily: "gilroyMedium" },
          "& .MuiDialogTitle-root": { fontFamily: "gilroyBold" },
          "& .MuiButton-root": { fontFamily: "gilroySemiBold" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "14px" }}>
          {documentMode === "invoice_and_po" ? "PO & Invoice Items" : "Invoice Items"}
        </DialogTitle>

        <DialogContent>
          {documentMode === "invoice_and_po" && (
            <Tabs
              value={itemsTab}
              onChange={(_, next) => setItemsTab(next)}
              sx={{
                borderBottom: "1px solid #e2e8f0",
                mb: 2,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontFamily: "gilroyBold",
                  fontSize: "14px",
                },
              }}
            >
            <Tab value="po" label="PO Items" />
              <Tab value="invoice" label="Invoice Items" />
             
            </Tabs>
          )}

          {itemsTab === "invoice" && (
            <TableContainer component={Paper}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Sl.No" },
                      { label: "Item Code" },
                      { label: "Item Name" },
                      { label: "Description" },
                      { label: "HSN" },
                      { label: "Quantity", align: "right" },
                      { label: "Unit Rate", align: "right" },
                      { label: "Total", align: "right" },
                      { label: "Tax", align: "right" },
                      { label: "Taxable Amount", align: "right" },
                    ].map((h) => (
                      <TableCell
                        key={h.label}
                        align={h.align}
                        sx={{
                          fontWeight: 600,
                          backgroundColor: "#e0e9ee",
                          fontSize: "14px",
                          whiteSpace: h.align ? "nowrap" : undefined,
                        }}
                      >
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {extractedData?.line_items.map((item) => (
                    <TableRow key={item.slNo} hover>
                      <TableCell sx={{ fontSize: "14px" }}>{item.slNo}</TableCell>
                      <TableCell sx={{ fontSize: "14px" }}>
                        {displayOrDash(item.code)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px" }}>
                        {displayOrDash(item.name)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px" }}>
                        {displayOrDash(item.description)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px" }}>
                        {displayOrDash(item.hsn)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {displayOrDash(item.qty)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {displayOrDash(item.unitRate)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#075b97",
                          fontFamily: "gilroyBold",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {(() => {
                          const unitRate = parseMoneyNumber(item.unitRate);
                          const quantity = parseMoneyNumber(item.qty);
                          if (unitRate === null || quantity === null) return "-";
                          return formatAmount(
                            unitRate * quantity,
                            extractedData?.currency
                          );
                        })()}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {displayOrDash(item.tax)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#075b97",
                          fontFamily: "gilroyBold",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatAmount(item.taxableAmount, extractedData?.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {itemsTab === "po" && documentMode === "invoice_and_po" && (
            <TableContainer component={Paper}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Sl.No" },
                      { label: "Item Code" },
                      { label: "Item Name" },
                      { label: "Description" },
                      { label: "Quantity", align: "right" },
                      { label: "Unit Rate", align: "right" },
                      { label: "Total", align: "right" },
                    ].map((h) => (
                        <TableCell
                          key={h.label}
                          align={h.align}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: "#e0e9ee",
                            fontSize: "14px",
                            whiteSpace: h.align ? "nowrap" : undefined,
                          }}
                        >
                          {h.label}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getPoLineItems(extractedData).map((item) => (
                    <TableRow key={item.slNo} hover>
                      <TableCell sx={{ fontSize: "14px" }}>{item.slNo}</TableCell>
                      <TableCell sx={{ fontSize: "14px" }}>
                        {displayOrDash(item.code)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px" }}>
                        {displayOrDash(item.name)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px" }}>
                        {displayOrDash(item.description)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {displayOrDash(item.qty)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {displayOrDash(item.unitRate)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#075b97",
                          fontFamily: "gilroyBold",
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatAmount(
                          item.total,
                          extractedData?.purchase_order_currency ||
                            extractedData?.currency
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {documentMode === "invoice_and_po" &&
            itemsTab === "po" &&
            getPoLineItems(extractedData).length === 0 && (
              <Typography
                sx={{
                  fontFamily: "gilroyMedium",
                  fontSize: "14px",
                  color: "#64748b",
                }}
              >
                No PO line items returned.
              </Typography>
            )}
        </DialogContent>

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", padding: "10px" }}
        >
          <Button
            variant="contained"
            onClick={() => setOpenLineItemModal(false)}
            sx={{ fontSize: "12px", fontWeight: "bold" }}
          >
            close
          </Button>
        </Box>
      </Dialog>

      <Modal open={statusModalOpen}>
        <Fade in={statusModalOpen}>
          <Box>
            <InvoiceStatusCard
              variant={statusModalContent.variant}
              title={statusModalContent.title}
              description={statusModalContent.description}
              onClose={closeStatusModal}
            />
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default AddInvoice;
